import { useState } from 'react'
import { KBFlex, KBText, KBButton, KBIcon } from '@/components/atoms'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  isTrialAtom,
  trialDaysRemainingAtom,
  trialUrgencyAtom,
  isTrialExpiredAtom,
  shouldShowTrialBannerAtom,
} from '@/store/trial.atom'
import { isSandboxModeAtom, currentTierAtom } from '@/store/pricing.atom'
import { useNavigate } from 'react-router-dom'
import { useAnalytics } from '@/hooks/useAnalytics'
import { cn } from '@/utils/tailwind.helper'

export interface KBTrialBannerProps {
  /** Banner mode: 'top' for full-width top banner, 'compact' for sidebar */
  mode?: 'top' | 'compact'
  /** Additional className */
  className?: string
  /** Callback when upgrade is clicked */
  onUpgrade?: () => void
  /** Whether banner can be dismissed */
  dismissible?: boolean
}

/**
 * KBTrialBanner - Trial status banner (RFC-001)
 *
 * Displays trial status with:
 * - Days remaining
 * - Urgency-based styling (info/warning/urgent)
 * - CTA to upgrade
 *
 * Modes:
 * - 'top': Full-width banner at top of page (kibo-ui style)
 * - 'compact': Small card for sidebar
 */
const KBTrialBanner = (props: KBTrialBannerProps) => {
  const { mode = 'top', className = '', onUpgrade, dismissible = false } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const [isDismissed, setIsDismissed] = useState(false)

  const shouldShow = useAtomValue(shouldShowTrialBannerAtom)
  const isTrial = useAtomValue(isTrialAtom)
  const isExpired = useAtomValue(isTrialExpiredAtom)
  const isSandbox = useAtomValue(isSandboxModeAtom)
  const daysRemaining = useAtomValue(trialDaysRemainingAtom)
  const urgency = useAtomValue(trialUrgencyAtom)
  const currentTier = useAtomValue(currentTierAtom)

  if (!shouldShow || isDismissed) return null

  const handleUpgradeClick = () => {
    track('trial_banner.clicked', {
      banner_mode: mode,
      days_remaining: daysRemaining,
      urgency,
      is_expired: isExpired,
      is_sandbox: isSandbox,
      current_tier: currentTier,
    })

    if (onUpgrade) {
      onUpgrade()
    } else {
      navigate('/admin/space-settings/billing')
    }
  }

  const handleDismiss = () => {
    track('trial_banner.dismissed', {
      banner_mode: mode,
      days_remaining: daysRemaining,
      urgency,
    })
    setIsDismissed(true)
  }

  const getIcon = () => {
    if (isExpired || isSandbox) return 'triangle-alert'
    switch (urgency) {
      case 'urgent':
        return 'circle-alert'
      case 'warning':
        return 'clock'
      default:
        return 'sparkles'
    }
  }

  const getMessage = () => {
    const days = daysRemaining ?? 14
    if (isExpired) {
      return t('trial.banner.expired')
    }
    if (isSandbox) {
      return t('trial.banner.sandbox')
    }
    if (daysRemaining !== null) {
      if (daysRemaining <= 1) {
        return t('trial.banner.endsToday')
      }
      if (daysRemaining <= 3) {
        return t('trial.banner.daysLeft', { days })
      }
      return t('trial.banner.premiumTrial', { days })
    }
    return t('trial.banner.explore')
  }

  // Bright, prominent colors for visibility
  const getColorClasses = () => {
    if (isExpired || isSandbox) {
      return {
        bg: 'tw-bg-amber-500',
        text: 'tw-text-white',
        icon: 'tw-text-white',
        buttonType: 'default' as const,
      }
    }
    switch (urgency) {
      case 'urgent':
        return {
          bg: 'tw-bg-red-500',
          text: 'tw-text-white',
          icon: 'tw-text-white',
          buttonType: 'default' as const,
        }
      case 'warning':
        return {
          bg: 'tw-bg-amber-500',
          text: 'tw-text-white',
          icon: 'tw-text-white',
          buttonType: 'default' as const,
        }
      default:
        return {
          bg: 'tw-bg-blue-600',
          text: 'tw-text-white',
          icon: 'tw-text-white',
          buttonType: 'default' as const,
        }
    }
  }

  const colors = getColorClasses()

  // Top banner mode - full width at top of page
  if (mode === 'top') {
    return (
      <div
        className={cn(
          'tw-box-border tw-w-full tw-px-4 tw-py-2.5',
          colors.bg,
          className
        )}
      >
        <KBFlex justify="space-between" align="center" gap={16}>
          <KBFlex align="center" gap={8}>
            <KBIcon name={getIcon()} size={16} className={colors.icon} />
            <KBText className={cn('tw-text-sm tw-font-medium', colors.text)}>
              {getMessage()}
            </KBText>
          </KBFlex>

          <KBFlex align="center" gap={8} className="tw-flex-shrink-0">
            <KBButton
              type={colors.buttonType}
              size="small"
              onClick={handleUpgradeClick}
              className="tw-border-white tw-bg-white tw-text-gray-900 hover:tw-bg-gray-100"
            >
              {isExpired
                ? t('trial.banner.upgradeNow')
                : t('trial.banner.viewPlans')}
            </KBButton>

            {dismissible && (
              <KBButton
                type="text"
                size="small"
                icon={<KBIcon name="x" size={14} className="tw-text-white" />}
                onClick={handleDismiss}
                className="tw-text-white hover:tw-bg-white/20"
              />
            )}
          </KBFlex>
        </KBFlex>
      </div>
    )
  }

  // Compact mode for sidebar
  return (
    <div
      className={cn(
        'tw-mx-3 tw-mb-3 tw-rounded-lg tw-p-3',
        colors.bg,
        className
      )}
    >
      <KBFlex vertical gap={8}>
        <KBFlex align="center" gap={8}>
          <KBIcon name={getIcon()} size={16} className={colors.icon} />
          <KBText className={cn('tw-text-sm tw-font-medium', colors.text)}>
            {isTrial && daysRemaining !== null
              ? t('trial.banner.daysLeftCompact', { days: daysRemaining })
              : isExpired
                ? t('trial.banner.trialExpired')
                : t('trial.banner.sandboxMode')}
          </KBText>
        </KBFlex>
        <KBButton
          type="default"
          size="small"
          block
          onClick={handleUpgradeClick}
          className="tw-border-white tw-bg-white tw-text-gray-900 hover:tw-bg-gray-100"
        >
          {t('trial.banner.upgrade')}
        </KBButton>
      </KBFlex>
    </div>
  )
}

export default KBTrialBanner
